import type React from "react";
import { useState } from "react";
import { Box, Card, CardContent } from "@mui/material";
import { Proposal } from "../types/proposal.types";
import { Comment } from "./memorial-page-preview/types/comments";
import MemorialHeader from "./memorial-page-preview/MemorialHeader";
import StreamThumbnail from "./memorial-page-preview/StreamThumbnail";
import { formatDateToLongString } from "../../../utils/FormatDate";
import ArrowLabel from "./memorial-page-preview/ArrowLabel";
import MemorialTabs from "./memorial-page-preview/MemorialTabs";
import CommentsList from "./memorial-page-preview/CommentsLists";

interface MemorialPageProps {
  proposal: Proposal;
}

const dummyComments: Comment[] = [
  {
    id: 1,
    author: "Joe Fishing",
    text: "From when we first met at Chawuri until I said goodbye last May Louis was a special friend. He helped me a lot & I like to think that I might have helped him a little. We shared many fishing experiences & I can't think of a bad one. Even with no fish and damaged boats they were all good times. Louis was a true gentleman, in every sense of the word, I shall miss him.",
    date: "November 6, 2025 at 4:30 PM",
  },
];

const MemorialPage: React.FC<MemorialPageProps> = ({ proposal }) => {
  const [activeTab, setActiveTab] = useState(1);
  const [comments, setComments] = useState<Comment[]>(dummyComments);
  const [newComment, setNewComment] = useState("");

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      const comment: Comment = {
        id: comments.length + 1,
        author: "Anonymous User",
        text: newComment,
        date: new Date().toLocaleString(),
      };
      setComments([...comments, comment]);
      setNewComment("");
    }
  };

  const deceasedFirstName =
    proposal.booking?.deceased?.[0]?.first_name ||
    proposal.deceasedName?.split(" ")[0] ||
    "Unknown";
  const deceasedLastName =
    proposal.booking?.deceased?.[0]?.surname ||
    proposal.deceasedName?.split(" ").slice(1).join(" ") ||
    "";
  const birthDate =
    proposal.booking?.deceased?.[0]?.date_of_birth ||
    proposal.deceasedBirthDate ||
    "";
  const deathDate =
    proposal.booking?.deceased?.[0]?.date_of_death ||
    proposal.deceasedPassedDate ||
    "";
  const serviceDate =
    proposal.booking?.services?.[0]?.date_of_service ||
    proposal.booking?.events?.[0]?.start_date_of_event ||
    "";
  const serviceTime =
    proposal.booking?.services?.[0]?.start_time ||
    proposal.booking?.events?.[0]?.start_time ||
    "09:00";
  const venueName =
    proposal.booking?.services?.[0]?.venue?.name ||
    proposal.booking?.events?.[0]?.venue?.name ||
    "";
  const venueCity =
    proposal.booking?.services?.[0]?.venue?.city ||
    proposal.booking?.events?.[0]?.venue?.city ||
    "";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
      }}
    >
      <Box sx={{ position: "relative", mt: -6, px: 4, pb: 4 }}>
        <MemorialHeader
          firstName={deceasedFirstName}
          lastName={deceasedLastName}
        />

        <StreamThumbnail
          firstName={deceasedFirstName}
          lastName={deceasedLastName}
          birthDate={birthDate ? formatDateToLongString(birthDate) : "Unknown"}
          deathDate={deathDate ? formatDateToLongString(deathDate) : "Unknown"}
          serviceDate={
            serviceDate ? formatDateToLongString(serviceDate) : "Unknown"
          }
          serviceTime={serviceTime}
          location={`${venueName}${venueCity ? `, ${venueCity}` : ""}`}
        />

        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Card
            sx={{
              width: "100%",
              maxWidth: "680px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: "8px",
            }}
          >
            <MemorialTabs activeTab={activeTab} onTabChange={handleTabChange} />

            <CardContent sx={{ p: 2 }}>
              <CommentsList
                comments={comments}
                newComment={newComment}
                onCommentChange={setNewComment}
                onAddComment={handleAddComment}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default MemorialPage;
